import { Box, Grid, List, Stack, Typography } from '@mui/material';
import React, { ReactElement, useEffect, useMemo } from 'react';
import FixList from './ListItemSingleFix';
import DocumentModal, { inferDocumentModal } from './DocumentModal';
import { useLocation } from 'react-router-dom';
import {
    RawData,
    RawDocument,
} from '../../listing-packets/components/VirtualizedTable';
import DateFnsAdapter from '@date-io/date-fns';
import { FieldArray, useFormikContext } from 'formik';
import { FormikPropsFixPacketValues } from '../FixPacketPage';
import { ManagePacketService } from 'services/ManagePacketService';
import { formattingDate } from 'shared/helpers/FormatDate';
import MissingList from './ListItemMissingCategory';

const dateFns = new DateFnsAdapter();
const SingleFix = (props: DetailSingleFix): ReactElement => {
    const {
        unexpectedType,
        groupingUpdate,
        groups,
        onActiveReworksItem,
        onDisableReworksItem,
    } = props;
    const { values } = useFormikContext<FormikPropsFixPacketValues>();
    const [categoryData, setCategoryData] = React.useState<string[]>([]);
    const [categoryDefaultValue, setCategoryDefaultValue] =
        React.useState<string>();
    const [categoryMenuName, setCategoryMenuName] = React.useState<string>();
    const [categoryIndex, setCategoryIndex] = React.useState<number>();
    const [singleDocument, setDocuments] = React.useState<RawDocument>();
    const documentModalRef = React.useRef<inferDocumentModal | null>(null);

    /**
     * Action to open specific document modal
     * @param {number} id - indices in documents data
     */
    const actionOpenModalOnListButton = React.useCallback((id: number) => {
        if (values.documents.some((o, i) => i === id)) {
            setCategoryMenuName(`documents[${id}].category`);
            setCategoryIndex(id);
            setCategoryDefaultValue(values.documents[id].category);
            setDocuments(values.documents[id]);
        }
        if (documentModalRef.current !== null) {
            documentModalRef.current.setOpen();
        }
    }, []);
    const location = useLocation();
    const defaultState = useMemo(() => {
        if (typeof location.state === 'object' && location.state !== null) {
            return location.state;
        }
        return {};
    }, [location.state]);
    const {
        actions,
        // updatedAtDifference,
        createdAtDifference,
        slaDateDifference,
        // reviewRequired,
        // surName,
        // uniqueRef,
        reason,
        clientId,
        bin,
        loanNumber,
        packetStatus,
        packetType,
        clientName,
        // operatorId,
        operatorName,
        completedDate,
        escalatedDate,
        uploadedDate,
        // recordNo,
    } = defaultState as RawData;

    useEffect(() => {
        ManagePacketService.getCategory({ clientId }).then((result) => {
            if (result.data && Array.isArray(result.data.data)) {
                setCategoryData(result.data.data);
            }
        });
    }, [clientId]);

    const getOperatorName = React.useCallback((operatorName: any) => {
        const lastName =
            typeof operatorName === 'object' && operatorName !== null
                ? operatorName.last
                : '';
        const firstName =
            typeof operatorName === 'object' && operatorName !== null
                ? operatorName.first
                : '';
        const nameOperator = String(firstName).substring(0, 1).toUpperCase();

        return typeof operatorName === 'object' && operatorName !== null
            ? `${lastName}, ${nameOperator}`
            : '-';
    }, []);
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const expectedMissingDocuments = useMemo(
        () => defaultState.missingDocumentCategoryList,
        [location.state]
    );

    if (location.state === null) return <div />;

    return (
        <Box className="fixpacket-content">
            <Box className="fixpacket-header">
                <Grid container spacing={2} className="fixpacket-container">
                    <Grid
                        container
                        item
                        spacing={1}
                        xs={12}
                        className="fixpacket-row"
                    >
                        <Grid container item spacing={0} xs={12} sm={6} md={4}>
                            <Grid item xs={6} sm={12}>
                                Cross Check Status
                            </Grid>
                            <Grid
                                item
                                xs={6}
                                sm={12}
                                data-testid="packet-status"
                            >
                                {packetStatus}
                            </Grid>
                        </Grid>
                        <Grid container item spacing={0} xs={12} sm={6} md={1}>
                            <Grid item xs={6} sm={12}>
                                SLA
                            </Grid>
                            <Grid item xs={6} sm={12} data-testid="slaTimely">
                                {`${
                                    slaDateDifference < 0
                                        ? slaDateDifference * -1
                                        : slaDateDifference || 0
                                } ${
                                    slaDateDifference < 0
                                        ? 'days ago'
                                        : 'days left'
                                }`}
                            </Grid>
                        </Grid>
                        <Grid container item spacing={0} xs={12} sm={6} md={3}>
                            <Grid item xs={6} sm={12}>
                                Checklist
                            </Grid>
                            <Grid item xs={6} sm={12} data-testid="clientname">
                                {clientName}
                            </Grid>
                        </Grid>
                        <Grid container item spacing={0} xs={12} sm={6} md={2}>
                            <Grid item xs={6} sm={12}>
                                Reference Number
                            </Grid>
                            <Grid item xs={6} sm={12} data-testid="binnumber">
                                {bin}
                            </Grid>
                        </Grid>
                        <Grid container item spacing={0} xs={12} sm={6} md={2}>
                            <Grid item xs={6} sm={12} className="loan">
                                Application Number
                            </Grid>
                            <Grid
                                item
                                xs={6}
                                sm={12}
                                data-testid="loannumber"
                                className="loan"
                            >
                                {loanNumber}
                            </Grid>
                        </Grid>
                    </Grid>
                    <Grid
                        container
                        item
                        spacing={1}
                        xs={12}
                        className="fixpacket-row"
                    >
                        <Grid container item spacing={0} xs={12} sm={6} md={4}>
                            <Grid item xs={6} sm={12}>
                                Uploaded
                            </Grid>
                            <Grid
                                item
                                xs={6}
                                sm={12}
                                data-testid="uploadeddate"
                            >
                                {`${formattingDate(
                                    uploadedDate
                                )} (${createdAtDifference} days ago)`}
                            </Grid>
                        </Grid>
                        <Grid container item spacing={0} xs={12} sm={6} md={1}>
                            <Grid item xs={6} sm={12}>
                                Cross Check Type
                            </Grid>
                            <Grid item xs={6} sm={12} data-testid="packettype">
                                {packetType}
                            </Grid>
                        </Grid>
                        <Grid
                            container
                            item
                            spacing={0}
                            xs={12}
                            sm={6}
                            md={1.5}
                        >
                            <Grid item xs={6} sm={12}>
                                Member ID
                            </Grid>
                            <Grid item xs={6} sm={12} data-testid="operatorid">
                                {getOperatorName(operatorName)}
                            </Grid>
                        </Grid>
                        <Grid
                            container
                            item
                            spacing={0}
                            xs={12}
                            sm={6}
                            md={1.5}
                        >
                            <Grid item xs={6} sm={12}>
                                Escalated age
                            </Grid>
                            <Grid
                                item
                                xs={6}
                                sm={12}
                                data-testid="escalatedage"
                            >
                                {escalatedDate
                                    ? `${dateFns.getDiff(
                                          new Date(),
                                          new Date(escalatedDate),
                                          'days'
                                      )} days ago`
                                    : '-'}
                            </Grid>
                        </Grid>
                        {/* <Grid container item spacing={0} xs={12} sm={6} md={2}>
                            <Grid item xs={6} sm={12}>
                                Unique Ref Id
                            </Grid>
                            <Grid item xs={6} sm={12} data-testid="cm9record">
                                {recordNo}
                            </Grid>
                        </Grid> */}
                        <Grid container item spacing={0} xs={12} sm={6} md={2}>
                            <Grid item xs={6} sm={12}>
                                Date completed
                            </Grid>
                            <Grid
                                item
                                xs={6}
                                sm={12}
                                data-testid="completeddate"
                            >
                                {completedDate
                                    ? `${formattingDate(completedDate)}`
                                    : '-'}
                            </Grid>
                        </Grid>
                    </Grid>
                    {reason && (
                        <Grid
                            container
                            item
                            spacing={1}
                            xs={12}
                            className="fixpacket-row-reason"
                        >
                            <Grid item xs={12}>
                                <Typography className="fixpacket-reason">
                                    Reason
                                </Typography>
                            </Grid>
                            <Grid item xs={12}>
                                <Typography
                                    className="fixpacket-reason-desc"
                                    data-testid="reasoncolumn"
                                >
                                    {reason}
                                </Typography>
                            </Grid>
                        </Grid>
                    )}
                </Grid>
            </Box>
            <Box className="fixpacket-listing">
                <List className="fix-list">
                    <FieldArray
                        name="documents"
                        render={(fields) => (
                            <>
                                {values.documents.map(
                                    (element, index: number) => (
                                        <FixList
                                            onCheckedSendToRework={(
                                                checked: boolean
                                            ) => {
                                                const { form } = fields;
                                                if (form) {
                                                    const {
                                                        getFieldHelpers,
                                                        values: valuesForm,
                                                    } = form;
                                                    const sendToRework =
                                                        getFieldHelpers(
                                                            `documents[${index}].sendToRework`
                                                        );
                                                    if (
                                                        checked === false &&
                                                        valuesForm.missingDocuments.findIndex(
                                                            (e: any) =>
                                                                e.sendToRework ===
                                                                true
                                                        ) === -1 &&
                                                        valuesForm.documents.findIndex(
                                                            (e: any) =>
                                                                e.sendToRework ===
                                                                true
                                                        ) === -1
                                                    ) {
                                                        if (
                                                            typeof onActiveReworksItem ===
                                                            'function'
                                                        ) {
                                                            onActiveReworksItem();
                                                        }
                                                    } else if (
                                                        checked === true &&
                                                        valuesForm.missingDocuments.filter(
                                                            (e: any) =>
                                                                e.sendToRework ===
                                                                true
                                                        ).length === 0 &&
                                                        valuesForm.documents.filter(
                                                            (e: any) =>
                                                                e.sendToRework ===
                                                                true
                                                        ).length === 1
                                                    ) {
                                                        if (
                                                            typeof onDisableReworksItem ===
                                                            'function'
                                                        ) {
                                                            onDisableReworksItem();
                                                        }
                                                    }
                                                    sendToRework.setValue(
                                                        !checked
                                                    );
                                                }
                                            }}
                                            isSendToRework={
                                                element.sendToRework
                                            }
                                            name={`documents[${index}].category`}
                                            showCategory={
                                                unexpectedType === false
                                            }
                                            isCommon={
                                                element.group === 'common'
                                            }
                                            key={`${index} - ${element.actions.uuid}`}
                                            unexpectedFileType={
                                                typeof element.unexpectedFileType ===
                                                'boolean'
                                                    ? element.unexpectedFileType
                                                    : false
                                            }
                                            index={index}
                                            onViewDocument={
                                                actionOpenModalOnListButton
                                            }
                                            filename={element.placeholder}
                                            isWarned={
                                                element.category ===
                                                    'general' &&
                                                groupingUpdate === false
                                            }
                                            groupName={element.groupName}
                                            categoryData={categoryData}
                                            groupsSelectionName={`documents[${index}].groups`}
                                            canGroup={groupingUpdate}
                                            groupsData={groups}
                                            defaultValue={element.category}
                                        />
                                    )
                                )}
                            </>
                        )}
                    />
                </List>
                {expectedMissingDocuments &&
                    Array.isArray(expectedMissingDocuments) &&
                    expectedMissingDocuments.length > 0 && (
                        <>
                            <Grid
                                item
                                xs={12}
                                className="missing-categories-list"
                            >
                                <Stack
                                    direction="row"
                                    spacing={2}
                                    className="stack-missing"
                                >
                                    <Typography>
                                        Missing Category List
                                    </Typography>
                                </Stack>
                            </Grid>

                            <List className="not-expected-list">
                                <FieldArray
                                    name="missingDocuments"
                                    render={(fields) => (
                                        <>
                                            {values.missingDocuments.map(
                                                (element, index: number) => (
                                                    <MissingList
                                                        onCheckedSendToRework={(
                                                            checked: boolean
                                                        ) => {
                                                            const { form } =
                                                                fields;
                                                            if (form) {
                                                                const {
                                                                    getFieldHelpers,
                                                                    values: valuesForm,
                                                                } = form;
                                                                if (
                                                                    checked ===
                                                                        false &&
                                                                    valuesForm.missingDocuments.findIndex(
                                                                        (
                                                                            e: any
                                                                        ) =>
                                                                            e.sendToRework ===
                                                                            true
                                                                    ) === -1 &&
                                                                    valuesForm.documents.findIndex(
                                                                        (
                                                                            e: any
                                                                        ) =>
                                                                            e.sendToRework ===
                                                                            true
                                                                    ) === -1
                                                                ) {
                                                                    if (
                                                                        typeof onActiveReworksItem ===
                                                                        'function'
                                                                    ) {
                                                                        onActiveReworksItem();
                                                                    }
                                                                } else if (
                                                                    checked ===
                                                                        true &&
                                                                    valuesForm.missingDocuments.filter(
                                                                        (
                                                                            e: any
                                                                        ) =>
                                                                            e.sendToRework ===
                                                                            true
                                                                    ).length ===
                                                                        1 &&
                                                                    valuesForm.documents.filter(
                                                                        (
                                                                            e: any
                                                                        ) =>
                                                                            e.sendToRework ===
                                                                            true
                                                                    ).length ===
                                                                        0
                                                                ) {
                                                                    if (
                                                                        typeof onDisableReworksItem ===
                                                                        'function'
                                                                    ) {
                                                                        onDisableReworksItem();
                                                                    }
                                                                }
                                                                const sendToRework =
                                                                    getFieldHelpers(
                                                                        `missingDocuments[${index}].sendToRework`
                                                                    );
                                                                sendToRework.setValue(
                                                                    !checked
                                                                );
                                                            }
                                                        }}
                                                        isSendToRework={
                                                            element.sendToRework
                                                        }
                                                        category={`${element.category} (${element.group})`}
                                                    />
                                                )
                                            )}
                                        </>
                                    )}
                                />
                            </List>
                        </>
                    )}
            </Box>
            <DocumentModal
                binNumber={bin}
                packetId={actions.uuid}
                document={singleDocument}
                clientId={clientId}
                ref={documentModalRef}
                categoryDefaultValue={categoryDefaultValue}
                categoryMenuName={categoryMenuName}
                categoryIndex={categoryIndex}
                categoryData={categoryData}
                canCategorize={groupingUpdate === false}
            />
        </Box>
    );
};

export default SingleFix;
